/* eslint-disable @typescript-eslint/naming-convention */
import { useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    NumberInput,
    NumberInputField,
    VStack,
} from '@chakra-ui/react'

import { AllPlayersSearch } from './AllPlayersSearch'
import { CurrentActivePlayer, MissingPlayer, TPlayer } from '@/common/types'

export type TPlayerData = TPlayer | MissingPlayer

type PositionData = {
    colIndex?: number
    rowIndex?: number
    index?: number
}

export type TPlayerModalProps = {
    player: TPlayerData
    position?: PositionData
}

type AddPlayerModalProps = {
    onAddNewPlayer: (newPlayerData: TPlayerModalProps) => void

    currentActivePlayer?: CurrentActivePlayer
    takenShirtNumbers?: number[]
    isExistingPlayer?: boolean
    isSquadPlayer?: boolean
    enableCaptainEdit?: boolean
    isSubstitute: boolean
}

type NewPlayer = {
    id: string | null
    name?: string
    surname: string
    shirt_number?: number | '-'
    captain: boolean
}

const generatePlayerID = () => `front:fake:${Math.floor(1000 + Math.random() * 9000)}`

const defaultNewPlayer = {
    id: null,
    name: undefined,
    surname: '',
    shirt_number: undefined,
    captain: false,
    enableCaptainEdit: false,
}

export function AddPlayerModal({
    onAddNewPlayer,
    currentActivePlayer,
    takenShirtNumbers = [],
    isExistingPlayer,
    enableCaptainEdit,
    isSubstitute,
}: AddPlayerModalProps) {
    const [newPlayer, setNewPlayer] = useState<NewPlayer>({
        ...defaultNewPlayer,
        id: currentActivePlayer?.id ?? generatePlayerID(),
        name: currentActivePlayer?.name?.split(',')[1]?.trimStart(),
        surname: currentActivePlayer?.name?.split(',')[0] ?? '',
        shirt_number: currentActivePlayer?.shirt_number,
        captain: currentActivePlayer?.captain ?? false,
    })

    const isSurnameInvalid = !newPlayer?.surname?.trim().length

    const takenShirtNumWithoutExistingPlayer =
        isExistingPlayer && currentActivePlayer?.shirt_number
            ? takenShirtNumbers.filter(num => num !== currentActivePlayer?.shirt_number)
            : takenShirtNumbers

    const isShirtNumberTaken =
        typeof newPlayer?.shirt_number === 'number'
            ? takenShirtNumWithoutExistingPlayer.includes(newPlayer?.shirt_number)
            : false

    const isShirtNumberTooHigh = Boolean(typeof newPlayer?.shirt_number === 'number' && newPlayer?.shirt_number > 99)

    const isShirtNumberInvalid = isShirtNumberTooHigh || isShirtNumberTaken

    const isSaveDisabled = isSurnameInvalid

    const shirtInputErrorMessage = () => {
        if (isShirtNumberTaken) {
            return <FormErrorMessage>Player with that shirt number already exists!</FormErrorMessage>
        }

        if (isShirtNumberTooHigh) {
            return <FormErrorMessage>Shirt number can't be higher then 99.</FormErrorMessage>
        }

        return null
    }

    const getPlayerName = (surname: string, name?: string) =>
        name ? `${newPlayer?.surname}, ${newPlayer?.name}` : surname

    const handleSavePlayer = () => {
        if (currentActivePlayer?.colIndex !== undefined && currentActivePlayer?.rowIndex !== undefined) {
            onAddNewPlayer({
                player: {
                    id: newPlayer.id,
                    name: getPlayerName(newPlayer.surname, newPlayer.name),
                    shirt_number: newPlayer.shirt_number,
                    captain: newPlayer.captain,
                },
                position: {
                    rowIndex: currentActivePlayer.rowIndex,
                    colIndex: currentActivePlayer.colIndex,
                },
            })
        } else if (currentActivePlayer?.index !== undefined) {
            onAddNewPlayer({
                player: {
                    id: newPlayer.id,
                    name: getPlayerName(newPlayer.surname, newPlayer.name),
                    shirt_number: newPlayer.shirt_number,
                    captain: false,
                },
                position: {
                    index: currentActivePlayer.index,
                },
            })
        } else if (currentActivePlayer?.reason !== undefined) {
            onAddNewPlayer({
                player: {
                    ...currentActivePlayer,
                    ...{
                        id: newPlayer.id,
                        name: getPlayerName(newPlayer.surname, newPlayer.name),
                        shirt_number: newPlayer.shirt_number,
                        captain: newPlayer.captain,
                    },
                },
            })
        } else {
            onAddNewPlayer({
                player: {
                    ...currentActivePlayer,
                    ...{
                        name: getPlayerName(newPlayer.surname, newPlayer.name),
                        shirt_number: newPlayer.shirt_number,
                        captain: newPlayer.captain,
                    },
                },
            })
        }
    }

    const isEditPlayer = currentActivePlayer?.shirt_number

    const handleSearchPlayerSelect = player => {
        setNewPlayer({
            id: player.id,
            name: player.name,
            surname: player.surname,
            shirt_number: player.shirtNumber,
            captain: player.captain,
        })
    }

    const patchPlayer = (patchValues: Partial<NewPlayer>) => {
        setNewPlayer(previousNewPlayer => ({ ...previousNewPlayer, ...patchValues }))
    }

    return (
        <ModalContent className="add-player-modal">
            <ModalCloseButton top={{ base: 4, lg: 12 }} right={{ base: 5, lg: 12 }} />
            <ModalHeader
                fontWeight="extrabold"
                fontSize="xl"
                fontStyle="italic"
                px={{ base: 5, lg: 12 }}
                pt={{ base: 4, lg: 12 }}
                pb={4}>
                {isEditPlayer ? 'EDIT PLAYER' : 'CREATE NEW PLAYER'}
            </ModalHeader>
            <ModalBody px={{ base: 5, lg: 8 }} pb={12}>
                <VStack spacing={4}>
                    <FormControl isRequired isInvalid={isSurnameInvalid}>
                        <FormLabel>Player surname</FormLabel>
                        <Input
                            className="player-surname-input"
                            value={newPlayer?.surname ? newPlayer?.surname : ''}
                            placeholder="Surname"
                            onChange={e => patchPlayer({ surname: e.target.value })}
                        />
                        {isSurnameInvalid ? <FormErrorMessage>Player surname is required.</FormErrorMessage> : null}
                    </FormControl>
                    <FormControl>
                        <FormLabel>Player name</FormLabel>
                        <Input
                            className="player-name-input"
                            value={newPlayer?.name ? newPlayer?.name : ''}
                            placeholder="Name"
                            onChange={e => patchPlayer({ name: e.target.value })}
                        />
                    </FormControl>
                    <FormControl isInvalid={isShirtNumberInvalid}>
                        <FormLabel>Player shirt number</FormLabel>
                        <NumberInput
                            className="player-shirt-number-input"
                            min={-1}
                            max={99}
                            value={newPlayer.shirt_number || undefined}
                            onChange={e => {
                                const numberValue = parseInt(e, 10)
                                patchPlayer({
                                    shirt_number:
                                        !Number.isNaN(numberValue) && numberValue > 0 ? numberValue : undefined,
                                })
                            }}>
                            <NumberInputField placeholder="ShirtNumber" />
                            {shirtInputErrorMessage()}
                        </NumberInput>
                        {newPlayer.id && !newPlayer.id.startsWith('front:fake:') && (
                            <FormHelperText fontWeight="bold" mt={2} ml={2}>
                                ID: {newPlayer.id}
                            </FormHelperText>
                        )}
                    </FormControl>
                    {!isSubstitute && (
                        <FormControl>
                            <Box display="flex" alignItems="center">
                                <FormLabel mb={0}>Set as captain</FormLabel>
                                <Checkbox
                                    disabled={!enableCaptainEdit}
                                    defaultChecked={newPlayer?.captain}
                                    checked={newPlayer?.captain}
                                    onChange={e => {
                                        patchPlayer({ captain: Boolean(e.target.checked) })
                                    }}
                                />
                            </Box>
                            {!enableCaptainEdit && (
                                <FormHelperText>Only one captain could be selected on each team</FormHelperText>
                            )}
                        </FormControl>
                    )}
                    <AllPlayersSearch handlePlayerSelect={handleSearchPlayerSelect} />
                    <Button
                        className="add-player-modal-save-button"
                        variant="green"
                        width="100%"
                        mt={8}
                        isDisabled={isSaveDisabled}
                        onClick={() => handleSavePlayer()}>
                        Save
                    </Button>
                </VStack>
            </ModalBody>
        </ModalContent>
    )
}
